import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Eye, Package, ThumbsUp, Tag, Flag, ShieldAlert, X, Mail, MessageSquare, Send, Globe, ChevronLeft, ChevronRight, Star, ArrowLeft, Shield } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import ListingCard from '@/components/ListingCard';

const CONTACT_ICONS = { email: Mail, discord: MessageSquare, telegram: Send, website: Globe };

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [contactModal, setContactModal] = useState(false);
  const [contactData, setContactData] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [reportModal, setReportModal] = useState(false);
  const [report, setReport] = useState({ reason: 'fake_listing', description: '' });
  const [reportLoading, setReportLoading] = useState(false);
  const [relatedListings, setRelatedListings] = useState([]);

  useEffect(() => {
    setListing(null);
    setRelatedListings([]);
    setLoading(true);
    API.get(`/listings/${id}`).then(r => {
      setListing(r.data);
      setLoading(false);
      // Fetch related listings after we have the game_id
      if (r.data?.game_id) {
        API.get(`/listings?game_id=${r.data.game_id}&exclude_id=${r.data.id}&limit=6`)
          .then(res => setRelatedListings(res.data?.listings || []))
          .catch(() => {});
      }
    }).catch(() => { toast.error('Listing not found'); navigate('/browse'); });
  }, [id]);

  const handleReveal = async () => {
    if (!user) { toast.error('Please login to reveal contact info'); navigate('/auth'); return; }
    if (listing.already_revealed && contactData) { setContactModal(true); return; }
    setContactLoading(true);
    try {
      const { data } = await API.post(`/listings/${listing.id}/reveal`);
      setContactData(data);
      setListing(prev => ({ ...prev, contact_reveal_count: (prev.contact_reveal_count || 0) + 1, already_revealed: true }));
      setContactModal(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reveal contact');
    } finally { setContactLoading(false); }
  };

  const handleReport = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Login required'); return; }
    if (!report.description.trim()) { toast.error('Please describe the issue'); return; }
    setReportLoading(true);
    try {
      await API.post('/reports', { seller_id: listing.seller_id, listing_id: listing.id, ...report });
      toast.success('Report submitted. We will review it shortly.');
      setReportModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally { setReportLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-rm-bg flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-rm-red border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!listing) return null;
  const isAvailable = listing.quantity > 0 && listing.is_active;
  const imgs = listing.image_urls?.length ? listing.image_urls : (listing.image_url ? [listing.image_url] : []);

  return (
    <div className="min-h-screen bg-[#080808]">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 animate-fade-in">
        {/* Breadcrumb */}
        <div className="flex items-center gap-3 text-xs mb-8">
          <button onClick={() => navigate('/browse')} className="flex items-center gap-1.5 text-zinc-600 hover:text-white transition-colors">
            <ArrowLeft size={12} /> Browse
          </button>
          <span className="text-zinc-800">/</span>
          <span className="text-zinc-400 truncate max-w-[200px]">{listing.title}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* ===== IMAGE GALLERY ===== */}
          <div className="space-y-3">
            {/* Main image */}
            <div className="relative rounded-sm overflow-hidden bg-[#111] border border-[#1e1e1e] aspect-square">
              {imgs.length > 0 ? (
                <>
                  <img src={imgs[activeImg]} alt={listing.title}
                    className="w-full h-full object-cover transition-opacity duration-300"
                    data-testid="listing-image" />
                  {/* Top gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
                  {imgs.length > 1 && (
                    <>
                      <button onClick={() => setActiveImg(i => (i - 1 + imgs.length) % imgs.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/70 backdrop-blur-sm border border-white/10 rounded-sm flex items-center justify-center hover:border-rm-red/60 hover:bg-black/90 transition-all">
                        <ChevronLeft size={16} />
                      </button>
                      <button onClick={() => setActiveImg(i => (i + 1) % imgs.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/70 backdrop-blur-sm border border-white/10 rounded-sm flex items-center justify-center hover:border-rm-red/60 hover:bg-black/90 transition-all">
                        <ChevronRight size={16} />
                      </button>
                      {/* Dot indicators */}
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {imgs.map((_, i) => (
                          <button key={i} onClick={() => setActiveImg(i)}
                            className={`h-1 rounded-full cursor-pointer transition-all duration-300 ${i === activeImg ? 'w-6 bg-rm-red' : 'w-2 bg-white/30'}`} />
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                  <Package size={48} className="text-zinc-800" />
                  <p className="text-xs text-zinc-700">No image available</p>
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {imgs.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {imgs.map((url, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className={`w-16 h-16 shrink-0 rounded-sm overflow-hidden border-2 transition-all duration-200 ${
                      i === activeImg ? 'border-rm-red shadow-[0_0_10px_rgba(229,9,20,0.3)]' : 'border-[#222] hover:border-[#333]'
                    }`}>
                    <img src={url} alt={`View ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ===== LISTING DETAILS ===== */}
          <div className="space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Link to={`/browse?game=${listing.game_id}`}
                  className="text-[10px] font-rajdhani font-bold uppercase tracking-[0.15em] text-zinc-500 bg-white/[0.03] border border-white/[0.06] px-2.5 py-1 hover:border-rm-red/30 hover:text-rm-red transition-all">
                  {listing.game_name}
                </Link>
                {!isAvailable && (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-rm-red border border-rm-red/30 bg-rm-red/5 px-2.5 py-1">
                    Sold Out
                  </span>
                )}
              </div>
              <h1 className="font-rajdhani font-bold text-3xl md:text-4xl uppercase tracking-tight leading-tight" data-testid="listing-title">
                {listing.title}
              </h1>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="stat-card">
                <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-600 mb-1.5">Price</p>
                <p className="font-rajdhani font-bold text-2xl text-rm-red price-badge" data-testid="listing-price">${listing.price}</p>
              </div>
              <div className="stat-card">
                <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-600 mb-1.5">Quantity</p>
                <p className="font-rajdhani font-bold text-2xl text-white" data-testid="listing-quantity">{listing.quantity}</p>
              </div>
              <div className="stat-card">
                <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-600 mb-1.5">Views</p>
                <div className="flex items-center gap-1.5">
                  <Eye size={14} className="text-zinc-600" />
                  <p className="font-rajdhani font-bold text-2xl text-white">{listing.contact_reveal_count || 0}</p>
                </div>
              </div>
            </div>

            {/* Seller Card */}
            <div className="bg-[#0f0f0f] border border-[#1e1e1e] rounded-sm p-4 hover:border-[#2a2a2a] transition-colors">
              <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-600 mb-3">Seller</p>
              <div className="flex items-center justify-between">
                <Link to={`/sellers/${listing.seller_id}`} className="flex items-center gap-3 group" data-testid="seller-link">
                  {/* Avatar with ring */}
                  <div className="relative">
                    <div className="w-11 h-11 bg-rm-red/10 border border-rm-red/20 rounded-sm flex items-center justify-center group-hover:border-rm-red/50 transition-colors">
                      <span className="font-rajdhani font-bold text-lg text-rm-red">{listing.seller_username?.[0]?.toUpperCase()}</span>
                    </div>
                  </div>
                  <div>
                    <p className="font-rajdhani font-bold text-white text-base group-hover:text-rm-red transition-colors">{listing.seller_username}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <ThumbsUp size={10} className="text-green-500" />
                      <span className="text-xs text-green-500 font-medium">{listing.seller_rating}% positive</span>
                      <span className="text-xs text-zinc-600">({listing.seller_reviews_count} reviews)</span>
                    </div>
                  </div>
                </Link>
                <Link to={`/sellers/${listing.seller_id}`}
                  className="text-[10px] font-rajdhani font-semibold uppercase tracking-widest text-zinc-600 hover:text-white transition-colors border border-[#222] px-2.5 py-1.5 hover:border-[#333]">
                  Profile
                </Link>
              </div>
            </div>

            {/* Description */}
            {listing.description && (
              <div>
                <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-600 mb-2">Description</p>
                <p className="text-sm text-zinc-400 leading-relaxed" data-testid="listing-description">{listing.description}</p>
              </div>
            )}

            {/* Filter values */}
            {listing.filter_values && Object.keys(listing.filter_values).length > 0 && (
              <div>
                <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-600 mb-3">Item Details</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(listing.filter_values).map(([k, v]) => v && (
                    <span key={k} className="bg-white/[0.03] border border-white/[0.07] px-3 py-1.5 text-xs rounded-sm">
                      <span className="text-zinc-600">{k}: </span>
                      <span className="text-zinc-200 font-medium">{v}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {listing.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {listing.tags.map(t => (
                  <span key={t} className="flex items-center gap-1.5 bg-white/[0.02] border border-white/[0.05] px-2.5 py-1 text-[11px] text-zinc-500 rounded-sm">
                    <Tag size={9} /> {t}
                  </span>
                ))}
              </div>
            )}

            {/* CTA */}
            <div className="space-y-3 pt-2">
              <button onClick={handleReveal} disabled={!isAvailable || contactLoading} data-testid="reveal-contact-btn"
                className={`w-full py-4 font-rajdhani font-bold uppercase tracking-[0.15em] text-sm rounded-sm transition-all duration-200
                  ${isAvailable
                    ? 'btn-primary btn-cta'
                    : 'bg-[#111] text-zinc-700 cursor-not-allowed border border-[#1e1e1e]'}`}>
                {contactLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Loading...
                  </span>
                ) : !isAvailable ? 'Out of Stock' : listing.already_revealed ? 'Show Contact Again' : 'Show Seller Contact'}
              </button>
              <div className="flex items-center justify-center gap-1.5 text-[10px] text-zinc-700">
                <Shield size={10} className="text-rm-red/50" />
                RobloxMart does not process payments. Trade at your own risk.
              </div>
            </div>

            {/* Report */}
            {user && user.id !== listing.seller_id && (
              <div className="pt-1">
                <button onClick={() => setReportModal(true)} data-testid="report-btn"
                  className="flex items-center gap-1.5 text-xs text-zinc-700 hover:text-zinc-400 transition-colors">
                  <Flag size={11} /> Report this listing
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== RELATED LISTINGS ===== */}
      {relatedListings.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 md:px-6 pb-12">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-rajdhani font-bold text-2xl uppercase tracking-tight">
              More from <span className="text-rm-red">{listing.game_name}</span>
            </h2>
            <Link to={`/browse?game=${listing.game_id}`}
              className="text-[10px] font-rajdhani font-bold uppercase tracking-widest text-zinc-600 hover:text-rm-red transition-colors">
              View all
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {relatedListings.map(l => <ListingCard key={l.id} listing={l} />)}
          </div>
        </div>
      )}

      {/* ===== CONTACT REVEAL MODAL ===== */}
      {contactModal && contactData && (
        <div className="fixed inset-0 modal-backdrop z-50 flex items-center justify-center p-4" data-testid="contact-modal">
          <div className="bg-[#0f0f0f] border border-[#252525] rounded-sm max-w-md w-full p-6 animate-scale-in shadow-[0_25px_60px_rgba(0,0,0,0.8)]">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-rajdhani font-bold text-xl uppercase tracking-wide">Seller Contact</h3>
                <p className="text-xs text-zinc-600 mt-0.5">for {listing.seller_username}</p>
              </div>
              <button onClick={() => setContactModal(false)} className="w-8 h-8 flex items-center justify-center text-zinc-600 hover:text-white hover:bg-white/5 rounded-sm transition-colors">
                <X size={16} />
              </button>
            </div>

            {/* Safety Warning */}
            <div className="bg-rm-red/5 border border-rm-red/20 rounded-sm p-3.5 mb-3">
              <div className="flex gap-2.5">
                <ShieldAlert size={15} className="text-rm-red shrink-0 mt-0.5" />
                <p className="text-xs text-zinc-500 leading-relaxed">
                  RobloxMart is not affiliated with Roblox Corporation. We do not process payments. <strong className="text-zinc-300">Trade at your own risk.</strong>
                </p>
              </div>
            </div>

            {/* Discord MM */}
            <div className="bg-[#5865F2]/5 border border-[#5865F2]/20 rounded-sm p-3.5 mb-5">
              <p className="text-[10px] font-rajdhani font-bold uppercase tracking-widest text-[#7289da] mb-1.5">Safety Recommendation</p>
              <p className="text-xs text-zinc-500 leading-relaxed">
                For high-value trades, use a <strong className="text-zinc-300">trusted middleman (MM)</strong>. Join our Discord for free MM services.
              </p>
              <a href="https://discord.gg/robloxmart" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-2.5 text-xs text-[#7289da] hover:text-[#9aa7ff] transition-colors font-medium">
                <MessageSquare size={11} /> Join Discord — Free MM Service
              </a>
            </div>

            {/* Contact Methods */}
            <div className="space-y-2.5">
              {contactData.contact_methods?.map((c, i) => {
                const Icon = CONTACT_ICONS[c.type] || Mail;
                return (
                  <div key={i} className="flex items-center gap-3 bg-white/[0.02] border border-white/[0.06] rounded-sm p-3.5 hover:border-white/10 transition-colors">
                    <div className="w-9 h-9 bg-rm-red/10 border border-rm-red/20 rounded-sm flex items-center justify-center shrink-0">
                      <Icon size={15} className="text-rm-red" />
                    </div>
                    <div>
                      <p className="text-[9px] text-zinc-600 uppercase tracking-widest mb-0.5">{c.type}</p>
                      <p className="text-sm font-medium text-white">{c.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <button onClick={() => setContactModal(false)} data-testid="contact-modal-close"
              className="w-full btn-primary py-3 rounded-sm mt-5 text-sm">
              Got It
            </button>
          </div>
        </div>
      )}

      {/* ===== REPORT MODAL ===== */}
      {reportModal && (
        <div className="fixed inset-0 modal-backdrop z-50 flex items-center justify-center p-4" data-testid="report-modal">
          <div className="bg-[#0f0f0f] border border-[#252525] rounded-sm max-w-md w-full p-6 animate-scale-in shadow-[0_25px_60px_rgba(0,0,0,0.8)]">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-rajdhani font-bold text-xl uppercase text-rm-red tracking-wide">Report Listing</h3>
              <button onClick={() => setReportModal(false)} className="w-8 h-8 flex items-center justify-center text-zinc-600 hover:text-white hover:bg-white/5 rounded-sm transition-colors">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleReport} className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 block mb-2">Reason</label>
                <select value={report.reason} onChange={e => setReport({ ...report, reason: e.target.value })}
                  className="w-full bg-[#0a0a0a] border border-[#222] text-white text-sm px-3 py-2.5 rounded-sm" data-testid="report-reason">
                  <option value="fake_listing">Fake Listing</option>
                  <option value="scam">Scam Attempt</option>
                  <option value="inappropriate">Inappropriate Content</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 block mb-2">Description</label>
                <textarea value={report.description} onChange={e => setReport({ ...report, description: e.target.value })}
                  placeholder="Describe the issue in detail..." rows={4} data-testid="report-description"
                  className="w-full bg-[#0a0a0a] border border-[#222] text-white placeholder:text-zinc-700 px-3 py-2.5 rounded-sm text-sm resize-none" />
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setReportModal(false)} className="flex-1 btn-outline py-2.5 text-sm rounded-sm">Cancel</button>
                <button type="submit" disabled={reportLoading} data-testid="report-submit"
                  className="flex-1 btn-primary py-2.5 text-sm rounded-sm disabled:opacity-50">
                  {reportLoading ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
